import { useState, useRef, useEffect } from "react";
import { createPortal } from "react-dom";
import { EllipsisVerticalIcon } from "@heroicons/react/24/outline";

export type ActionItem = {
  id: string;
  label: string;
  onClick: () => void;
  disabled?: boolean;
};

type ActionsMenuProps = {
  actions: ActionItem[];
};

type MenuCoords = {
  top: number;
  left: number;
  width: number;
};

const MENU_WIDTH = 224;
const GAP = 2;
const ESTIMATED_ITEM_HEIGHT = 40;

const ActionsMenu = ({ actions }: ActionsMenuProps) => {
  const [ isOpen, setIsOpen ] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const [ menuCoords, setMenuCoords ] = useState<MenuCoords | null>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node) &&
        buttonRef.current && !buttonRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    if (isOpen && buttonRef.current) {
      const updateCoords = () => {
        if (buttonRef.current) {
          const rect = buttonRef.current.getBoundingClientRect();
          const halfway = window.innerHeight / 2;
          const showAbove = rect.top >= halfway;
          const estimatedMenuHeight = GAP + (actions.length * ESTIMATED_ITEM_HEIGHT);

          const top = showAbove
            ? rect.top + window.scrollY - estimatedMenuHeight - GAP
            : rect.bottom + window.scrollY + GAP;

          setMenuCoords({
            top,
            left: rect.right + window.scrollX - MENU_WIDTH,
            width: rect.width,
          });
        }
      };
      updateCoords();

      const handleScroll = () => updateCoords();
      window.addEventListener("scroll", handleScroll, true);
      window.addEventListener("resize", updateCoords);
      return () => {
        window.removeEventListener("scroll", handleScroll, true);
        window.removeEventListener("resize", updateCoords);
      };
    }
  }, [ isOpen, actions.length ]);

  const handleActionClick = (action: ActionItem) => {
    if (!action.disabled) {
      action.onClick();
      setIsOpen(false);
    }
  };

  if (actions.length === 0) return null;

  const buttonClass = (disabled?: boolean) =>
    `inline-flex items-center justify-center px-3 py-2 rounded-lg text-sm font-medium border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 active:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-brand-500 focus:ring-offset-2 touch-manipulation whitespace-nowrap ${
      disabled ? "opacity-50 cursor-not-allowed pointer-events-none" : ""
    }`;

  return (
    <>
      <div className="flex flex-wrap items-center gap-2 lg:hidden">
        {actions.map((action) => (
          <button
            key={action.id}
            type="button"
            onClick={() => {
              if (!action.disabled) action.onClick();
            }}
            disabled={action.disabled}
            className={buttonClass(action.disabled)}
          >
            {action.label}
          </button>
        ))}
      </div>

      <div className="relative hidden lg:inline-block">
        <button
          type="button"
          ref={buttonRef}
          onClick={(e) => {
            e.stopPropagation();
            setIsOpen((prev) => !prev);
          }}
          className="p-2 rounded-lg text-gray-500 hover:text-gray-700 hover:bg-gray-100 transition-colors touch-manipulation"
          aria-label="Actions menu"
        >
          <EllipsisVerticalIcon className="w-5 h-5" />
        </button>

        {isOpen && menuCoords &&
          createPortal(
            <div
              className="absolute z-50"
              style={{
                top: menuCoords.top,
                left: menuCoords.left,
                width: MENU_WIDTH,
              }}
              ref={dropdownRef}
            >
              <div className="bg-white rounded-lg shadow-lg border border-gray-200">
                <div className="py-1 divide-y divide-gray-200">
                  {actions.map((action) => {
                    return (
                      <button
                        type="button"
                        key={action.id}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleActionClick(action);
                        }}
                        disabled={action.disabled}
                        className={`w-full flex items-center px-4 py-2 text-sm text-left transition-colors ${
                          action.disabled
                            ? "text-gray-400 cursor-not-allowed"
                            : "text-gray-700 hover:bg-gray-50 active:bg-gray-100"
                        }`}
                      >
                        <span>{action.label}</span>
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>,
            document.body
          )}
      </div>
    </>
  );
};

export default ActionsMenu;
